// StarNode represents a star on the map.

import CollectStarActionResult from 'app/src/engine/action-results/CollectStarActionResult';
import { useGameStateContext } from 'app/src/game/GameStateContext';
import scheduleAnimation from 'app/src/utils/scheduleAnimation';
import useValueChanged from 'app/src/utils/useValueChanged';
import * as React from 'react';

type Props = {
	row: number;
	col: number;
};

enum AnimationStage {
	Pulsing,
	PreCollected,
	Collected,
}

export default function StarNode({ row, col }: Props): React.ReactElement | null {
	const [animationStage, setAnimationStage] = React.useState<AnimationStage>(
		AnimationStage.Pulsing,
	);

	const { gameStateStore } = useGameStateContext();

	// When we're running animations, we need to be able to cancel.
	const cancelAnimationSequenceRef = React.useRef<(() => void) | null>(null);

	// If this component is unmounted (this happens when the user resets), kill
	// any ongoing animations.
	React.useEffect(
		() => (): void => {
			if (cancelAnimationSequenceRef.current) {
				cancelAnimationSequenceRef.current();
			}
		},
		[],
	);

	// Handle animation changes.
	const currentActionResultSetChanged = useValueChanged(gameStateStore.currentActionResultSet);
	React.useEffect(() => {
		const { currentActionResultSet } = gameStateStore;

		if (currentActionResultSetChanged && currentActionResultSet) {
			// Is there an action result in the current set intended for this node?
			const actionResult = currentActionResultSet.find(
				(result) =>
					result instanceof CollectStarActionResult &&
					row === result.star.location.row &&
					col === result.star.location.col,
			);

			if (!actionResult) {
				return;
			}

			// If the action result is ours, animate the star being collected.
			setAnimationStage(AnimationStage.PreCollected);

			cancelAnimationSequenceRef.current = scheduleAnimation([
				[
					(): void => {
						setAnimationStage(AnimationStage.Collected);

						// Resolve immediately. The animation will actually take another
						// 300ms to finish, but we don't want the robot to pause while it's
						// playing.
						gameStateStore.completeAnimation();
						cancelAnimationSequenceRef.current = null;
					},
					10,
				],
			]);
		}
	}, [gameStateStore, currentActionResultSetChanged, row, col]);

	return (
		<div
			className={[
				'StarNode',
				animationStage === AnimationStage.Pulsing && 'StarNode--pulsing',
				animationStage === AnimationStage.Collected && 'StarNode--collected',
			]
				.filter(Boolean)
				.join(' ')}
		/>
	);
}
